import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import FoodDetailModal from './FoodDetailModal';
import FoodEditModal from './FoodEditModal';
import AppleCarousel from './AppleCarousel';
import LoadingSpinner from './common/LoadingSpinner';
import ConfirmationModal from './ConfirmationModal';
import { useFeedbackModal } from './common/useFeedbackModal';

function FoodManagement() {
  const { t } = useTranslation();
  const [foods, setFoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFood, setSelectedFood] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingFood, setEditingFood] = useState(null);
  const { modal, openModal, closeModal } = useFeedbackModal();
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'warning',
    onConfirm: null
  });

  useEffect(() => {
    loadFoods();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadFoods = async () => {
    setLoading(true);
    try {
      const data = await api.getAllFoodItems();
      setFoods(data);
    } catch (error) {
      console.error('Error loading foods:', error);
      openModal({
        title: 'Load failed',
        message: 'Failed to load foods.',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFoodClick = (food) => {
    setSelectedFood(food);
    setShowDetailModal(true);
  };

  const handleEdit = (food) => {
    setEditingFood(food);
    setShowDetailModal(false);
    setShowEditModal(true);
  };

  const handleSave = async (formData) => {
    try {
      if (editingFood) {
        await api.updateFoodItem(editingFood.id, formData);
      } else {
        await api.createFoodItem(formData);
      }
      setShowEditModal(false);
      setEditingFood(null);
      loadFoods();
    } catch (error) {
      console.error('Error saving food:', error);
      openModal({
        title: 'Save failed',
        message: 'Failed to save food.',
        type: 'error'
      });
    }
  };

  const handleDelete = async (id) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete food',
      message: 'Are you sure you want to delete this food item?',
      type: 'warning',
      onConfirm: async () => {
        try {
          await api.deleteFoodItem(id);
          setShowDetailModal(false);
          setSelectedFood(null);
          loadFoods();
        } catch (error) {
          console.error('Error deleting food:', error);
          openModal({
            title: 'Delete failed',
            message: 'Failed to delete food.',
            type: 'error'
          });
        }
      }
    });
  };

  const handleAddNew = () => {
    setEditingFood(null);
    setShowEditModal(true);
  };

  const getStatusEmoji = (status) => {
    switch (status) {
      case 'SAFE': return '✅';
      case 'UNSAFE': return '❌';
      case 'CAUTION': return '⚠️';
      default: return '❓';
    }
  };

  if (loading) {
    return (
      <div className="food-management">
        <LoadingSpinner message="Loading foods..." />
      </div>
    );
  }

  return (
    <div className="food-management">
      <div className="section-header">
        <h2>🍽️ {t('food.management.title', 'Food Management')}</h2>
        <button className="btn-primary" onClick={handleAddNew}>
          ➕ Add Food
        </button>
      </div>

      {foods.length === 0 ? (
        <p className="empty-state">📭 No foods found. Add your first food item!</p>
      ) : (
        <div className="food-management-carousel-wrapper">
          <AppleCarousel>
            {foods.map((food) => (
              <div
                key={food.id}
                className={`popular-food-card ${food.safetyStatus?.toLowerCase()}`}
                onClick={() => handleFoodClick(food)}
              >
                <div className="food-name">{getStatusEmoji(food.safetyStatus)} {food.name}</div>
                <div className={`safety-badge ${food.safetyStatus?.toLowerCase()}`}>
                  {food.safetyStatus}
                </div>
                {food.reason && (
                  <div className="food-reason-preview">
                    {food.reason.length > 60 ? food.reason.substring(0, 60) + '...' : food.reason}
                  </div>
                )}
              </div>
            ))}
          </AppleCarousel>
        </div>
      )}

      <FoodDetailModal
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedFood(null);
        }}
        food={selectedFood}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <FoodEditModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingFood(null);
        }}
        food={editingFood}
        onSave={handleSave}
      />

      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm || (() => {})}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        confirmText="Continue"
        cancelText="Cancel"
      />

      <ConfirmationModal
        isOpen={modal.isOpen}
        onClose={closeModal}
        onConfirm={() => {}}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        confirmText="OK"
        cancelText=""
      />
    </div>
  );
}

export default FoodManagement;

